import React from 'react';
import { Form, InputNumber } from 'antd';
import { ColumnType } from 'antd/es/table';
import { priceFormatter } from 'common/helpers/format.helper';
import { EUpdateStatus } from 'common/models/commonModel';
import { Check, Spinner } from 'common/svg';
import {
  EPurchaseOrderStatus,
  EPurchaseOrderStatusFilterKey,
  EPurchaseOrderStatusFilterLabel,
  EPurchaseOrderStatusLabel,
  IPurchaseOrder,
  IPurchaseOrderRecoBasisModel,
  IPurchaseOrderRecoProduct,
  IPurchaseOrderRecoStoreModel,
  IRecoBasisDataSources,
} from 'entities/PurchaseOrder/PurchaseOrder.models';

export const purchaseOrderNumberBuilder = (number: string) => {
  return `#SO${number}`;
};

export const mapPurchaseOrdersToKanbanDataSource = (data: IPurchaseOrder[]) => {
  const statuses = Object.values(EPurchaseOrderStatus);

  const map = new Map<string, IPurchaseOrder[]>();

  statuses.forEach((item) => {
    let key: string = item;

    if (item === EPurchaseOrderStatus.Delivered || item === EPurchaseOrderStatus.Canceled) {
      key = EPurchaseOrderStatusFilterKey.DeliveredCanceled;
    }

    if (!map.has(key)) {
      map.set(key, []);
    }

    const items = data.filter((i) => i.status === item);

    map.get(key)?.push(...items);
  });

  return Array.from(map).map(([key, items]) => {
    return {
      key,
      title:
        key === EPurchaseOrderStatusFilterKey.DeliveredCanceled
          ? EPurchaseOrderStatusFilterLabel[key as EPurchaseOrderStatusFilterKey]
          : EPurchaseOrderStatusLabel[key as EPurchaseOrderStatus],
      items,
    };
  });
};

// MBC-85: Track PO Kanban data source = RECO column (left) + 4 status columns.
// Items are a discriminated union; renderer narrows by 'sonum' in item.
export type TrackPoKanbanItem = IPurchaseOrder | IPurchaseOrderRecoStoreModel;

export const mapTrackPoKanbanDataSource = (
  orders: IPurchaseOrder[],
  recoStores: IPurchaseOrderRecoStoreModel[],
) => {
  const statusColumns = mapPurchaseOrdersToKanbanDataSource(orders);
  return [
    {
      key: EPurchaseOrderStatusFilterKey.Reco,
      title: EPurchaseOrderStatusFilterLabel[EPurchaseOrderStatusFilterKey.Reco],
      items: recoStores as TrackPoKanbanItem[],
    },
    ...statusColumns.map((col) => ({ ...col, items: col.items as TrackPoKanbanItem[] })),
  ];
};

export const getGeneratePoConfig = (isMobile: boolean) => {
  if (isMobile) {
    return [
      {
        key: 'category',
        dataIndex: 'category',
        title: (
          <div className="flex flex__column">
            <span>Category/</span>
            <span>Product Set</span>
          </div>
        ),
        render: (_, record) => {
          return (
            <div>
              <div className="ff-regular fs-xxxs color-gray-1">{record.category}</div>
              <span className="ff-regular fs-xxs">{record.name}</span>
            </div>
          );
        },
        width: '25%',
      },
      {
        key: 'recoCount',
        dataIndex: 'recoCount',
        title: 'Reco QTY',
        width: '20%',
        onHeaderCell: () => {
          return {
            style: {
              textWrap: 'nowrap',
              textAlign: 'center',
            },
          };
        },
        onCell: () => {
          return { style: { textAlign: 'center' } };
        },
        render: (_, record) => <span className="ff-regular fs-xxs color-gray-1">{record.recoCount}</span>,
      },
      {
        key: 'orderCount',
        dataIndex: 'orderCount',
        title: 'Order QTY',
        width: '20%',
        onHeaderCell: () => {
          return {
            style: {
              textWrap: 'nowrap',
              textAlign: 'center',
            },
          };
        },
        onCell: () => {
          return { style: { textAlign: 'center' } };
        },
        render: (_, record) => {
          return (
            <Form.Item name={[record?.key, 'orderCount']} style={{ margin: 0 }}>
              <InputNumber min={1} controls={false} />
            </Form.Item>
          );
        },
      },
      {
        key: 'totalAmount',
        dataIndex: 'totalAmount',
        title: (
          <div className="flex flex__column">
            <span>Unit Amount/</span>
            <span>Total Amount</span>
          </div>
        ),
        width: '35%',
        onHeaderCell: () => {
          return { style: { textAlign: 'end' } };
        },
        onCell: () => {
          return { style: { textAlign: 'end' } };
        },
        render: (_, record) => {
          return (
            <div>
              <div className="ff-regular fs-xxxs color-gray-1">{priceFormatter(record.price)}</div>
              <span className="ff-regular fs-xxs">{priceFormatter(record.orderCount * record.price)}</span>
            </div>
          );
        },
      },
    ] as ColumnType<IPurchaseOrderRecoProduct>[];
  }

  return [
    {
      key: 'category',
      dataIndex: 'category',
      title: 'Category',
      width: '12%',
      ellipsis: true,
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Product Set',
      width: '12%',
      ellipsis: true,
    },
    {
      key: 'price',
      dataIndex: 'price',
      title: 'Unit Amount',
      width: '16%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
      render: (text) => priceFormatter(text),
    },
    {
      key: 'recoCount',
      dataIndex: 'recoCount',
      title: 'Reco QTY',
      width: '12%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
    },
    {
      key: 'orderCount',
      dataIndex: 'orderCount',
      title: 'Order QTY',
      width: '12%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end', color: '#3359ff' } }),
      onCell: () => ({ style: { textAlign: 'end', color: '#3359ff' } }),
      render: (_, record) => {
        return (
          <Form.Item name={[record?.key, 'orderCount']} style={{ margin: 0 }}>
            <InputNumber min={1} controls={false} />
          </Form.Item>
        );
      },
    },
    {
      key: 'totalAmount',
      title: 'Total Amount',
      width: '16%',
      ellipsis: true,
      render: (_, record) => priceFormatter(record.orderCount * record.price),
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
    },
    {
      key: 'status',
      dataIndex: 'status',
      render: (_, record) => {
        if (!record.status) {
          return null;
        }

        return (
          <div className="flex flex-align-center">
            {record.status === EUpdateStatus.Saved && <Check className="icon-purple" />}
            {record.status === EUpdateStatus.Saving && <Spinner className="icon-purple" />}
            <span className="ml-2 ff-regular fs-xxs color-purple">
              {record.status === EUpdateStatus.Saving ? 'Saving...' : 'Saved!'}
            </span>
          </div>
        );
      },
      width: '20%',
    },
  ] as ColumnType<IPurchaseOrderRecoProduct>[];
};

export const buildRecoBasisDataSources = (recoBasis: IPurchaseOrderRecoBasisModel | null): IRecoBasisDataSources => {
  const dataSources: IRecoBasisDataSources = {
    unitsSoldDataSource: [],
    trendDataSource: [],
    adjDemandDataSource: [],
    sellingPeriodDataSource: [],
    productSetDataSource: [],
    safetyStockDataSource: [],
  };

  if (!recoBasis) {
    return dataSources;
  }

  dataSources.unitsSoldDataSource.push({
    key: 'unitsSold',
    criteria: 'Units Sold',
    sun: recoBasis.unitsSold.sun,
    mon: recoBasis.unitsSold.mon,
    tue: recoBasis.unitsSold.tue,
    wed: recoBasis.unitsSold.wed,
    thu: recoBasis.unitsSold.thu,
    fri: recoBasis.unitsSold.fri,
    sat: recoBasis.unitsSold.sat,
  });
  dataSources.trendDataSource.push({
    key: 'trend',
    last4Wk: recoBasis.trend.last4Wk,
    last8Wk: recoBasis.trend.last8Wk,
    trendFactor: recoBasis.trend.trendFactor,
  });
  dataSources.adjDemandDataSource.push({
    key: 'adjDemand',
    criteria: 'Adj. Demand',
    sun: recoBasis.adjDemand.sun,
    mon: recoBasis.adjDemand.mon,
    tue: recoBasis.adjDemand.tue,
    wed: recoBasis.adjDemand.wed,
    thu: recoBasis.adjDemand.thu,
    fri: recoBasis.adjDemand.fri,
    sat: recoBasis.adjDemand.sat,
  });
  dataSources.sellingPeriodDataSource.push({
    key: 'sellingPeriod',
    sellingPeriod: `${recoBasis.sellingPeriod.firstDay} to ${recoBasis.sellingPeriod.lastDay}`,
    periodSum: recoBasis.periodSum,
    currentInventory: recoBasis.currentInv,
    initialReco: recoBasis.initialReco,
  });
  dataSources.productSetDataSource.push({
    key: 'productSet',
    productSet: recoBasis.productSet,
    adjInitialReco: recoBasis.adjInitialReco,
  });
  dataSources.safetyStockDataSource.push({
    key: 'safetyStock',
    sellingPeriod: `${recoBasis.sellingPeriod.firstDay} to ${recoBasis.sellingPeriod.lastDay}`,
    safetyStock: recoBasis.safetyStock,
    adjInitialReco: Number(recoBasis.adjInitialReco) + Number(recoBasis.safetyStock),
    reco: recoBasis.reco,
  });

  return dataSources;
};
